import { useMemo } from 'react';
import clsx from 'clsx';
import { Field, Form as FormikForm, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { mixed, object, string } from 'yup';
import { Container, Typography } from '@material-ui/core';

import SubmitWithLoading from '$app-web/components/submit-with-loading';
import { ROLES } from '$app-web/constants';
import { useSignUp } from '$app-web/modules/auth/hooks';
import CustomInputTextField from '$app-web/modules/auth/pages/sign-up/components/custom-input-text-field';
import { getIsACT, isResearcher, reportUserSignUp } from '$app-web/utils';

import useStyles from './styles';

import { FontAwesomeIcon } from '$common/fontawesome-pro/react-fontawesome';

const initialValues = {
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
    role: ROLES.PARTICIPANT,
    organization: '',
};

const Form = () => {
    const { role } = useParams();

    const { signUp, isLoading } = useSignUp({
        onSuccess: () => {
            if (!isResearcher(role)) {
                reportUserSignUp();
            }
        },
    });

    const classes = useStyles();
    const { t } = useTranslation();

    const isACT = useMemo(() => getIsACT(), []);
    const optionalTextForACT = isACT && role === ROLES.PARTICIPANT ? ` (${t('pdash:signup_name_optional')})` : '';

    const validationSchema = useMemo(() => {
        let firstName = string().trim().max(30, 'First Name should be at most 30 characters long.');

        let lastName = string().trim().max(30, 'Last Name should be at most 30 characters long.');

        if (isResearcher(role)) {
            firstName = firstName.required('You should enter your first name.');
            lastName = lastName.required('You should enter your last name.');
        }

        return object().shape({
            email: string().email().required('You should enter email.'),
            firstName,
            lastName,
            role: mixed().oneOf([ROLES.PARTICIPANT, ROLES.RESEARCHER]).required(),
            organization: string(),
            password: string().newPassword(),
            confirmPassword: string().newPasswordConfirmation('password'),
        });
    }, [role]);

    return (
        <Container maxWidth="xs">
            <Formik
                onSubmit={(values, actions) => signUp({ ...values, role }, actions)}
                initialValues={initialValues}
                validationSchema={validationSchema}
            >
                {({ isValid }) => (
                    <FormikForm>
                        <Field
                            name="firstName"
                            placeholder={`${t('pdash:signup_first_name')}${optionalTextForACT}`}
                            type="text"
                            component={CustomInputTextField}
                            fullWidth
                            margin="normal"
                            startAdornment={<FontAwesomeIcon icon={['fal', 'user-alt']} />}
                            showHelperTextOnError={false}
                        />
                        <Field
                            name="lastName"
                            placeholder={`${t('pdash:signup_last_name')}${optionalTextForACT}`}
                            type="text"
                            component={CustomInputTextField}
                            fullWidth
                            margin="normal"
                            startAdornment={<FontAwesomeIcon icon={['fal', 'user-alt']} />}
                            showHelperTextOnError={false}
                        />
                        {isResearcher(role) && (
                            <Field
                                name="organization"
                                placeholder={t('pdash:signup_organization')}
                                type="text"
                                component={CustomInputTextField}
                                fullWidth
                                margin="normal"
                                startAdornment={<FontAwesomeIcon icon={['fal', 'landmark']} />}
                                showHelperTextOnError={false}
                            />
                        )}
                        <Field
                            name="email"
                            placeholder={t('pdash:email_address')}
                            type="email"
                            component={CustomInputTextField}
                            fullWidth
                            margin="normal"
                            startAdornment={<FontAwesomeIcon icon={['fal', 'envelope']} />}
                            showHelperTextOnError={false}
                        />
                        <Field
                            name="password"
                            placeholder={t('pdash:password')}
                            type="password"
                            component={CustomInputTextField}
                            fullWidth
                            margin="normal"
                            startAdornment={<FontAwesomeIcon icon={['fal', 'lock-alt']} />}
                        />
                        <Field
                            name="confirmPassword"
                            placeholder={t('pdash:confirm_password')}
                            type="password"
                            component={CustomInputTextField}
                            fullWidth
                            margin="normal"
                            startAdornment={<FontAwesomeIcon icon={['fal', 'lock-alt']} />}
                        />
                        <div className={classes.submitButtonWrapper}>
                            <SubmitWithLoading
                                classes={{
                                    submitButton: isACT ? 'containedACTColor' : undefined,
                                }}
                                text={t('pdash:login_signup_text')}
                                isSubmitting={isLoading}
                                disabled={!isValid}
                                buttonProps={{
                                    variant: 'contained',
                                    color: 'secondary',
                                    fullWidth: true,
                                }}
                            />
                        </div>
                        <Typography
                            variant="subtitle2"
                            gutterBottom
                            className={clsx(classes.TOSWrapper, {
                                [classes.actHealthColors]: isACT,
                            })}
                            align="center"
                            color="textPrimary"
                            dangerouslySetInnerHTML={{ __html: t('pdash:signup_tos') }}
                        />
                    </FormikForm>
                )}
            </Formik>
        </Container>
    );
};

export default Form;
